import { useEffect, useState } from "react";
import api, { errMsg, formatTL, formatNumber } from "@/lib/api";
import PageHeader from "@/components/app/PageHeader";
import KPICard from "@/components/app/KPICard";
import ExportButtons from "@/components/app/ExportButtons";
import { Building2, Users, TrendingUp, FileSpreadsheet, Search, Plus, X, Pencil, Trash2, Target, Award, Upload } from "lucide-react";
import { toast } from "sonner";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, Legend, CartesianGrid } from "recharts";

const STATUS_META = {
  lead: { label: "Aday", color: "#F59E0B", bg: "bg-[#FEF3C7]", text: "text-[#F59E0B]" },
  prospect: { label: "Görüşme", color: "#0020C2", bg: "bg-[#DBEAFE]", text: "text-[#0020C2]" },
  active: { label: "Aktif Müşteri", color: "#16A34A", bg: "bg-[#DCFCE7]", text: "text-[#16A34A]" },
  lost: { label: "Kaybedildi", color: "#DC2626", bg: "bg-[#FEE2E2]", text: "text-[#DC2626]" },
};
const PIE_COLORS = ["#F59E0B", "#0020C2", "#16A34A", "#DC2626"];

const empty = () => ({ name: "", sector: "", contact_person: "", phone: "", email: "", address: "", status: "lead", notes: "" });

export default function MarketingPage() {
  const [summary, setSummary] = useState(null);
  const [statusFilter, setStatusFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty());

  const load = async () => {
    try {
      const { data } = await api.get("/marketing/summary");
      setSummary(data);
    } catch (e) { toast.error(errMsg(e)); }
  };
  useEffect(() => { load(); }, []);

  const openCreate = () => { setEditing(null); setForm(empty()); setShowForm(true); };
  const openEdit = (c) => {
    setEditing(c);
    setForm({ name: c.name, sector: c.sector || "", contact_person: c.contact_person || "", phone: c.phone || "", email: c.email || "", address: c.address || "", status: c.status || "lead", notes: c.notes || "" });
    setShowForm(true);
  };
  const submit = async (e) => {
    e.preventDefault();
    try {
      if (editing) await api.put(`/companies/${editing.id}`, form);
      else await api.post("/companies", form);
      toast.success("Kaydedildi");
      setShowForm(false); load();
    } catch (err) { toast.error(errMsg(err)); }
  };
  const remove = async (c) => {
    if (!window.confirm(`${c.name} firmasını silmek istiyor musunuz?`)) return;
    try { await api.delete(`/companies/${c.id}`); toast.success("Silindi"); load(); }
    catch (err) { toast.error(errMsg(err)); }
  };
  const uploadXlsx = async (file) => {
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/companies/upload-xlsx", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`${data.inserted} firma yüklendi${data.skipped ? ` (${data.skipped} atlandı)` : ""}`);
      load();
    } catch (err) { toast.error(errMsg(err)); }
  };

  if (!summary) return <div className="p-8 text-sm text-neutral-500">Yükleniyor...</div>;

  const q = search.toLowerCase().trim();
  const companies = summary.companies.filter(c => {
    if (statusFilter !== "all" && c.status !== statusFilter) return false;
    if (!q) return true;
    return (c.name + " " + c.sector + " " + c.contact_person + " " + c.phone).toLowerCase().includes(q);
  });

  const pipelineData = Object.entries(summary.status_counts).map(([k, v]) => ({
    name: STATUS_META[k]?.label || k, count: v, key: k,
  }));

  return (
    <>
      <PageHeader
        title="Pazarlama"
        subtitle="Firma pipeline yönetimi, sektör analizi ve gelir attribüsyonu."
        testid="marketing-header"
        actions={
          <div className="flex items-center gap-2 flex-wrap">
            <ExportButtons
              filename="pazarlama_firmalari"
              title="Pazarlama Raporu"
              subtitle={`${summary.grand.company_count} firma · Toplam gelir: ${formatTL(summary.grand.lifetime_revenue)} · Kazanma oranı: %${summary.grand.win_rate}`}
              sections={[
                { name: "Firmalar", heading: "Tüm Firmalar (Gelir Sıralı)",
                  columns: [
                    { key: "name", label: "Firma" },
                    { key: "sector", label: "Sektör" },
                    { key: "status", label: "Durum", get: (r) => STATUS_META[r.status]?.label || r.status },
                    { key: "contact_person", label: "Yetkili" },
                    { key: "phone", label: "Telefon" },
                    { key: "personnel_count", label: "Personel", format: "num" },
                    { key: "routes_count", label: "Güzergah", format: "num" },
                    { key: "offers_count", label: "Teklif", format: "num" },
                    { key: "offers_won", label: "Kazanılan", format: "num" },
                    { key: "offers_value", label: "Teklif Değeri", format: "money" },
                    { key: "lifetime_revenue", label: "Yaşam Boyu Gelir", format: "money" },
                  ],
                  rows: summary.companies,
                },
                { name: "Sektör Analizi", heading: "Sektör Bazlı Toplam",
                  columns: [
                    { key: "sector", label: "Sektör" },
                    { key: "count", label: "Firma Sayısı", format: "num" },
                    { key: "revenue", label: "Gelir", format: "money" },
                    { key: "offer_value", label: "Teklif Değeri", format: "money" },
                  ],
                  rows: summary.sectors,
                },
              ]}
            />
            <label className="flex items-center gap-2 px-3 py-2.5 text-xs font-semibold uppercase tracking-[0.15em] border border-[#E5E5E5] hover:bg-[#F5F5F5] cursor-pointer">
              <Upload className="w-3.5 h-3.5" /> <span data-testid="marketing-xlsx-upload">Excel Yükle</span>
              <input type="file" accept=".xlsx" className="hidden" onChange={(e) => { const f = e.target.files?.[0]; if (f) uploadXlsx(f); e.target.value = ""; }} />
            </label>
            <button data-testid="marketing-add-btn" onClick={openCreate} className="flex items-center gap-2 bg-[#0A0A0A] text-white px-4 py-2.5 text-xs font-semibold uppercase tracking-[0.15em] hover:bg-[#0020C2]">
              <Plus className="w-4 h-4" strokeWidth={2.2} /> Yeni Firma
            </button>
          </div>
        }
      />

      {/* KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
        <KPICard testid="kpi-companies" label="Toplam Firma" value={formatNumber(summary.grand.company_count)} sub="Portföyünüzde" accent="info" icon={Building2} />
        <KPICard testid="kpi-personnel" label="Personel Sayısı" value={formatNumber(summary.grand.personnel_total)} sub="Aktif taşıma" accent="info" icon={Users} />
        <KPICard testid="kpi-offers" label="Teklifler" value={formatNumber(summary.grand.offers_total)} sub={`${summary.grand.offers_won} kazanıldı`} accent="warning" icon={Target} />
        <KPICard testid="kpi-win-rate" label="Kazanma Oranı" value={`%${summary.grand.win_rate}`} sub="Teklif başarısı" accent={summary.grand.win_rate >= 30 ? "success" : "warning"} icon={Award} />
        <KPICard testid="kpi-revenue" label="Yaşam Boyu Gelir" value={formatTL(summary.grand.lifetime_revenue)} sub="Firmalardan" accent="success" icon={TrendingUp} />
      </div>

      {/* Charts row */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-6">
        <div className="bg-white border border-[#E5E5E5] p-5 rounded-md">
          <div className="text-[11px] uppercase tracking-widest font-semibold text-neutral-500 mb-3">Pipeline Dağılımı</div>
          {pipelineData.reduce((a, x) => a + x.count, 0) === 0 ? (
            <div className="text-xs text-neutral-400 py-12 text-center">Veri yok</div>
          ) : (
            <ResponsiveContainer width="100%" height={220}>
              <PieChart>
                <Pie data={pipelineData} dataKey="count" nameKey="name" cx="50%" cy="50%" outerRadius={80} label={(e) => `${e.name}: ${e.count}`}>
                  {pipelineData.map((entry, i) => <Cell key={i} fill={STATUS_META[entry.key]?.color || PIE_COLORS[i % 4]} />)}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          )}
        </div>
        <div className="bg-white border border-[#E5E5E5] p-5 rounded-md lg:col-span-2">
          <div className="text-[11px] uppercase tracking-widest font-semibold text-neutral-500 mb-3">En Çok Gelir Getiren 10 Firma</div>
          {summary.top_companies.length === 0 ? (
            <div className="text-xs text-neutral-400 py-12 text-center">Gelir kaydı yok</div>
          ) : (
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={summary.top_companies} layout="vertical" margin={{ left: 10, right: 30 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E5E5E5" />
                <XAxis type="number" tickFormatter={(v) => `${(v/1000).toFixed(0)}k`} style={{ fontSize: 10 }} />
                <YAxis type="category" dataKey="name" width={140} style={{ fontSize: 10 }} />
                <Tooltip formatter={(v) => formatTL(v)} />
                <Legend wrapperStyle={{ fontSize: 10 }} />
                <Bar dataKey="lifetime_revenue" name="Yaşam Boyu Gelir" fill="#16A34A" />
                <Bar dataKey="offers_value" name="Teklif Değeri" fill="#0020C2" />
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>
      </div>

      {/* Sector analysis */}
      {summary.sectors.length > 0 && (
        <div className="bg-white border border-[#E5E5E5] p-5 rounded-md mb-6">
          <div className="text-[11px] uppercase tracking-widest font-semibold text-neutral-500 mb-3">Sektör Analizi</div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {summary.sectors.slice(0, 8).map(s => (
              <div key={s.sector} className="border border-[#F5F5F5] p-3 bg-[#FAFAFA]">
                <div className="text-xs font-semibold truncate">{s.sector}</div>
                <div className="text-[10px] text-neutral-500 mt-1"><span className="num font-semibold">{s.count}</span> firma</div>
                <div className="num text-sm font-bold mt-1 text-[#16A34A]">{formatTL(s.revenue)}</div>
                <div className="num text-[10px] text-[#0020C2]">Teklif: {formatTL(s.offer_value)}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="bg-white border border-[#E5E5E5] p-4 rounded-md mb-4 flex items-center gap-3 flex-wrap">
        <div className="flex items-center gap-2 flex-1 min-w-[240px]">
          <Search className="w-4 h-4 text-neutral-400" />
          <input data-testid="marketing-search" placeholder="Firma, sektör, yetkili ara..." value={search} onChange={e => setSearch(e.target.value)} className="flex-1 px-2 py-1.5 text-sm border-0 focus:outline-none" />
        </div>
        <div className="flex items-center gap-1">
          {["all", "lead", "prospect", "active", "lost"].map(s => (
            <button key={s} onClick={() => setStatusFilter(s)} data-testid={`filter-${s}`} className={`px-3 py-1.5 text-[10px] uppercase tracking-widest font-semibold border ${statusFilter === s ? "bg-[#0A0A0A] text-white border-[#0A0A0A]" : "border-[#E5E5E5] hover:bg-[#F5F5F5]"}`}>
              {s === "all" ? "Tümü" : STATUS_META[s]?.label}
              <span className="ml-1.5 num">{s === "all" ? summary.companies.length : summary.status_counts[s]}</span>
            </button>
          ))}
        </div>
      </div>

      {/* Companies list */}
      <div className="bg-white border border-[#E5E5E5] rounded-md overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-[#FAFAFA] border-b border-[#E5E5E5]">
              <tr>
                {["Firma","Sektör","Durum","Yetkili","Personel","Güzergah","Teklif","Gelir",""].map((h, i) => (
                  <th key={h} className={`px-3 py-3 text-[11px] uppercase tracking-widest text-neutral-500 font-semibold ${i >= 4 && i <= 7 ? "text-right" : "text-left"}`}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {companies.length === 0 ? (
                <tr><td colSpan={9} className="p-10 text-center text-sm text-neutral-500">Firma bulunamadı</td></tr>
              ) : companies.map(c => {
                const m = STATUS_META[c.status] || STATUS_META.lead;
                return (
                  <tr key={c.id} className="border-b border-[#F5F5F5]" data-testid={`company-row-${c.id}`}>
                    <td className="px-3 py-3">
                      <div className="font-semibold">{c.name}</div>
                      <div className="text-xs text-neutral-500">{c.phone} {c.email && `· ${c.email}`}</div>
                    </td>
                    <td className="px-3 py-3 text-neutral-600">{c.sector || "-"}</td>
                    <td className="px-3 py-3">
                      <span className={`inline-block px-2 py-0.5 text-[10px] uppercase tracking-widest font-semibold ${m.bg} ${m.text}`}>{m.label}</span>
                    </td>
                    <td className="px-3 py-3 text-neutral-600">{c.contact_person || "-"}</td>
                    <td className="px-3 py-3 text-right num">{formatNumber(c.personnel_count)}</td>
                    <td className="px-3 py-3 text-right num">{formatNumber(c.routes_count)}</td>
                    <td className="px-3 py-3 text-right num">
                      <span className="font-semibold">{c.offers_count}</span>
                      {c.offers_won > 0 && <span className="text-[#16A34A] text-[10px] ml-1">/{c.offers_won}✓</span>}
                    </td>
                    <td className="px-3 py-3 text-right num font-semibold text-[#16A34A]">{formatTL(c.lifetime_revenue)}</td>
                    <td className="px-3 py-3 text-right">
                      <div className="inline-flex gap-1">
                        <button data-testid={`company-edit-${c.id}`} onClick={() => openEdit(c)} className="p-1.5 hover:bg-[#F5F5F5]"><Pencil className="w-3.5 h-3.5" /></button>
                        <button data-testid={`company-delete-${c.id}`} onClick={() => remove(c)} className="p-1.5 hover:bg-[#DC2626] hover:text-white"><Trash2 className="w-3.5 h-3.5" /></button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {showForm && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={() => setShowForm(false)}>
          <div className="bg-white w-full max-w-2xl rounded-md shadow-lg" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between px-6 py-4 border-b border-[#E5E5E5]">
              <div>
                <div className="text-[10px] uppercase tracking-widest text-neutral-500">{editing ? "Düzenle" : "Yeni"}</div>
                <div className="font-heading font-bold text-lg">Firma</div>
              </div>
              <button onClick={() => setShowForm(false)} className="p-1.5 hover:bg-[#F5F5F5]"><X className="w-4 h-4" /></button>
            </div>
            <form onSubmit={submit} className="p-6 grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="md:col-span-2">
                <label className="text-xs font-semibold uppercase tracking-widest text-neutral-600">Firma Adı *</label>
                <input required value={form.name} onChange={e => setForm({...form, name: e.target.value})} className="input mt-1.5" />
              </div>
              <div>
                <label className="text-xs font-semibold uppercase tracking-widest text-neutral-600">Sektör</label>
                <input value={form.sector} onChange={e => setForm({...form, sector: e.target.value})} className="input mt-1.5" />
              </div>
              <div>
                <label className="text-xs font-semibold uppercase tracking-widest text-neutral-600">Durum</label>
                <select value={form.status} onChange={e => setForm({...form, status: e.target.value})} className="input mt-1.5">
                  {Object.entries(STATUS_META).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
                </select>
              </div>
              <div>
                <label className="text-xs font-semibold uppercase tracking-widest text-neutral-600">Yetkili Kişi</label>
                <input value={form.contact_person} onChange={e => setForm({...form, contact_person: e.target.value})} className="input mt-1.5" />
              </div>
              <div>
                <label className="text-xs font-semibold uppercase tracking-widest text-neutral-600">Telefon</label>
                <input value={form.phone} onChange={e => setForm({...form, phone: e.target.value})} className="input mt-1.5" />
              </div>
              <div className="md:col-span-2">
                <label className="text-xs font-semibold uppercase tracking-widest text-neutral-600">Email</label>
                <input type="email" value={form.email} onChange={e => setForm({...form, email: e.target.value})} className="input mt-1.5" />
              </div>
              <div className="md:col-span-2">
                <label className="text-xs font-semibold uppercase tracking-widest text-neutral-600">Adres</label>
                <input value={form.address} onChange={e => setForm({...form, address: e.target.value})} className="input mt-1.5" />
              </div>
              <div className="md:col-span-2">
                <label className="text-xs font-semibold uppercase tracking-widest text-neutral-600">Notlar</label>
                <textarea rows={3} value={form.notes} onChange={e => setForm({...form, notes: e.target.value})} className="input mt-1.5" />
              </div>
              <div className="md:col-span-2 flex justify-end gap-2 pt-4 border-t border-[#E5E5E5]">
                <button type="button" onClick={() => setShowForm(false)} className="px-4 py-2 text-xs uppercase tracking-widest font-semibold border border-[#E5E5E5] hover:bg-[#F5F5F5]">Vazgeç</button>
                <button type="submit" className="px-4 py-2 text-xs uppercase tracking-widest font-semibold bg-[#0A0A0A] text-white hover:bg-[#0020C2]">{editing ? "Kaydet" : "Oluştur"}</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </>
  );
}
